import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ChatController from "../controller/chatController";
import { attachSupportChatPusher } from "../services/supportChatPusher";
import { useToast } from "../contexts/ToastContext";
import {
  OPEN_CHAT_SUPPORT_DRAFT_KEY,
  OPEN_CHAT_SUPPORT_SESSION_KEY,
} from "../utils/supportChatNavigation";
import { getAllEWalletsAmount } from "../utils/apiClient";
import AppConstants from "../utils/appConstants";
import {
  buildDisplayMessages,
  enrichMessagesWithSentCache,
  loadRecentSentCacheIntoRef,
  mergeMessagesById,
  persistRecentSentMessages,
} from "../utils/supportChatMessageUtils";
import "./css/SupportChatWidget.css";

export default function SupportChatWidget({ hideLauncher = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatLoadingOlder, setChatLoadingOlder] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [chatUploadingAttachment, setChatUploadingAttachment] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const fileInputRef = useRef(null);
  const chatInputRef = useRef(null);
  const pendingDraftFocusRef = useRef(false);
  const [chatPagination, setChatPagination] = useState(null);
  const recentSentMessagesRef = useRef([]);
  const chatBodyRef = useRef(null);
  const chatBottomSentinelRef = useRef(null);
  const chatMaxLoadedPageRef = useRef(1);
  const loadOlderLockRef = useRef(false);
  const showErrorRef = useRef(null);
  /** When true, pin viewport to the newest message after open/reload. */
  const forceScrollToLatestRef = useRef(false);
  /** Block pagination-at-top until open scroll has finished (prevents starting on oldest page). */
  const allowLoadOlderRef = useRef(false);
  /** Bumps on effect cleanup so stale async work never skips setChatLoading(false) (fixes stuck "Loading..." under Strict Mode / remounts). */
  const chatFetchGenRef = useRef(0);
  const isChatOpenRef = useRef(false);
  const { error: showError } = useToast();
  showErrorRef.current = showError;

  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
    if (isChatOpen) {
      // Flutter ChatMessageController.getMessages(reload): zero local badge when chat opens.
      setUnreadCount(0);
    } else {
      allowLoadOlderRef.current = false;
      forceScrollToLatestRef.current = false;
    }
  }, [isChatOpen]);

  const scrollToBottomForced = useCallback(() => {
    const container = chatBodyRef.current;
    const sentinel = chatBottomSentinelRef.current;
    if (sentinel && typeof sentinel.scrollIntoView === "function") {
      try {
        sentinel.scrollIntoView({ block: "end", inline: "nearest", behavior: "auto" });
      } catch (_) {}
    }
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  const isPinnedToBottom = useCallback((thresholdPx = 4) => {
    const el = chatBodyRef.current;
    if (!el) return false;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= thresholdPx;
  }, []);

  useEffect(() => {
    loadRecentSentCacheIntoRef(recentSentMessagesRef);
  }, []);

  const currentUserIdStr = useMemo(() => {
    if (typeof window === "undefined") return "";
    return (
      localStorage.getItem("userId") ||
      sessionStorage.getItem("userId") ||
      ""
    );
  }, []);

  const resolvedChatMessages = useMemo(
    () => buildDisplayMessages(chatMessages, currentUserIdStr),
    [chatMessages, currentUserIdStr]
  );

  useEffect(() => {
    const shouldOpenFromRouteState = !!location?.state?.openChatSupport;
    const shouldOpenFromSession = (() => {
      try {
        return sessionStorage.getItem(OPEN_CHAT_SUPPORT_SESSION_KEY) === "1";
      } catch (_) {
        return false;
      }
    })();

    if (!shouldOpenFromRouteState && !shouldOpenFromSession) return;

    let draft = "";
    try {
      sessionStorage.removeItem(OPEN_CHAT_SUPPORT_SESSION_KEY);
      draft = String(sessionStorage.getItem(OPEN_CHAT_SUPPORT_DRAFT_KEY) || "").trim();
      sessionStorage.removeItem(OPEN_CHAT_SUPPORT_DRAFT_KEY);
    } catch (_) {}

    if (draft) {
      setChatInput(draft);
      pendingDraftFocusRef.current = true;
    }

    setIsChatOpen(true);

    try {
      navigate(`${location.pathname}${location.search || ""}`, {
        replace: true,
        state: {},
      });
    } catch (_) {}
  }, [location?.state?.openChatSupport, location.pathname, location.search, navigate]);

  useEffect(() => {
    const input = chatInputRef.current;
    if (!input || !isChatOpen) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
  }, [isChatOpen, chatInput]);

  useEffect(() => {
    if (!isChatOpen || !pendingDraftFocusRef.current) return;
    pendingDraftFocusRef.current = false;
    const input = chatInputRef.current;
    if (!input) return;
    input.focus();
    const end = input.value.length;
    try {
      input.setSelectionRange(end, end);
    } catch (_) {}
  }, [isChatOpen, chatInput]);

  /**
   * Sync unread badge from server CHAT_NOTIFICATIONS (Flutter InformationController /
   * EWalletController.updateEWalletsAmount). Never mark messages seen here — only update UI count.
   */
  useEffect(() => {
    const onChatBadge = (event) => {
      const count = Math.max(0, Number(event?.detail?.count) || 0);
      if (isChatOpenRef.current) {
        setUnreadCount(0);
        return;
      }
      setUnreadCount(count);
    };
    window.addEventListener("mfg:chat-notification-badge", onChatBadge);
    return () => window.removeEventListener("mfg:chat-notification-badge", onChatBadge);
  }, []);

  /**
   * Seed unread from server on mount so navigating away/back does not look "seen"
   * when the widget remounts with unreadCount=0 (Flutter keeps counter in InformationController).
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getAllEWalletsAmount();
        const data = response?.decryptedData || response?.data || {};
        if (cancelled) return;
        if (Number(data?.CODE) !== AppConstants.responseOk && Number(data?.CODE) !== 200) {
          return;
        }
        const chatCount = Math.max(0, Number(data?.NOTIFICATIONS?.CHAT_NOTIFICATIONS) || 0);
        if (!isChatOpenRef.current) {
          setUnreadCount(chatCount);
        }
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const appendIncomingByChatId = useCallback(async (chatId) => {
    if (!chatId) return false;
    try {
      const { message } = await ChatController.getMessageByChatId(chatId);
      if (!message) return false;
      const enriched = enrichMessagesWithSentCache(
        [message],
        recentSentMessagesRef.current
      );
      setChatMessages((prev) => mergeMessagesById(prev, enriched));
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  const refreshLatestMessagesQuiet = useCallback(async () => {
    try {
      const { messages, pagination } = await ChatController.getMessages({
        page: 1,
        perPage: 20,
      });
      setChatPagination((p) => ({ ...(p || {}), ...pagination }));
      const enriched = enrichMessagesWithSentCache(messages, recentSentMessagesRef.current);
      setChatMessages((prev) => mergeMessagesById(prev, enriched));
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  /**
   * Flutter: while chat is open, Pusher → get-a-message → append.
   * FCM alone does not append; if we only get FCM (no CHAT_ID), refresh page 1 as fallback.
   * While chat is closed: never call get-a-message / get-chat-message (those mark STATUS=1).
   */
  const handleIncomingChatMessage = useCallback(
    async (chatId) => {
      if (!isChatOpenRef.current) {
        // Prefer server badge via wallet sync; bump locally so launcher reacts immediately.
        setUnreadCount((count) => count + 1);
        return;
      }

      let appended = false;
      if (chatId) {
        appended = await appendIncomingByChatId(chatId);
      }
      if (!appended) {
        await refreshLatestMessagesQuiet();
      }

      requestAnimationFrame(() => {
        scrollToBottomForced();
        requestAnimationFrame(() => scrollToBottomForced());
      });
    },
    [appendIncomingByChatId, refreshLatestMessagesQuiet, scrollToBottomForced]
  );

  /**
   * Mirror Flutter ChatMessagesScreen: start Pusher only while chat is open; stop on close.
   * This prevents get-a-message (mark-seen) from running when the panel is closed.
   */
  useEffect(() => {
    if (!isChatOpen) return undefined;

    const uid = localStorage.getItem("userId") || sessionStorage.getItem("userId");
    if (uid == null || String(uid) === "") return undefined;

    const detach = attachSupportChatPusher(uid, (chatId) => {
      handleIncomingChatMessage(chatId);
    });

    return () => {
      detach();
    };
  }, [isChatOpen, handleIncomingChatMessage]);

  useEffect(() => {
    const onChatNotification = (event) => {
      const chatId = event?.detail?.chatId ?? null;
      handleIncomingChatMessage(chatId);
    };

    window.addEventListener("mfg:chat-incoming-message", onChatNotification);
    return () => window.removeEventListener("mfg:chat-incoming-message", onChatNotification);
  }, [handleIncomingChatMessage]);

  /**
   * When chat opens: same as Flutter getMessages(reload:true) — fetch page 1 (marks seen on server).
   */
  useEffect(() => {
    if (!isChatOpen) {
      setChatLoading(false);
      forceScrollToLatestRef.current = false;
      allowLoadOlderRef.current = false;
      return undefined;
    }

    const fetchId = ++chatFetchGenRef.current;
    forceScrollToLatestRef.current = true;
    allowLoadOlderRef.current = false;

    setChatLoading(true);
    setChatMessages([]);
    setChatPagination(null);
    chatMaxLoadedPageRef.current = 1;

    (async () => {
      try {
        const { messages, pagination } = await ChatController.getMessages({
          page: 1,
          perPage: 20,
        });
        if (chatFetchGenRef.current !== fetchId) return;
        setChatPagination(pagination);
        const enriched = enrichMessagesWithSentCache(messages, recentSentMessagesRef.current);
        setChatMessages(mergeMessagesById([], enriched));
      } catch (err) {
        if (chatFetchGenRef.current === fetchId) {
          showErrorRef.current?.(
            "Chat Error",
            err?.message || "Failed to load chat messages."
          );
        }
      } finally {
        if (chatFetchGenRef.current === fetchId) {
          setChatLoading(false);
        }
      }
    })();

    return () => {
      chatFetchGenRef.current += 1;
      setChatLoading(false);
    };
  }, [isChatOpen]);

  /**
   * Pin to newest message before paint (Flutter reverse ListView starts at latest).
   * Also suppress "load older" until pinned — otherwise scrollTop=0 on open fetches page 2+.
   */
  useLayoutEffect(() => {
    if (!isChatOpen || chatLoading) return undefined;
    if (!forceScrollToLatestRef.current) return undefined;

    if (resolvedChatMessages.length === 0) {
      forceScrollToLatestRef.current = false;
      allowLoadOlderRef.current = true;
      return undefined;
    }

    allowLoadOlderRef.current = false;
    scrollToBottomForced();

    let attempts = 0;
    let enableTimer = null;
    const timer = window.setInterval(() => {
      scrollToBottomForced();
      attempts += 1;
      if (isPinnedToBottom(2) || attempts >= 24) {
        window.clearInterval(timer);
        forceScrollToLatestRef.current = false;
        // Wait a tick so any scroll event from pinning does not trigger older-page load.
        enableTimer = window.setTimeout(() => {
          allowLoadOlderRef.current = true;
        }, 120);
      }
    }, 40);

    return () => {
      window.clearInterval(timer);
      if (enableTimer) window.clearTimeout(enableTimer);
    };
  }, [
    isChatOpen,
    chatLoading,
    resolvedChatMessages.length,
    scrollToBottomForced,
    isPinnedToBottom,
  ]);

  const loadOlderMessages = async () => {
    if (!allowLoadOlderRef.current) return;
    if (loadOlderLockRef.current || chatLoading || chatLoadingOlder) return;
    const nextPage = chatMaxLoadedPageRef.current + 1;
    const lastPage = Number(chatPagination?.lastPage ?? 1);
    if (!chatPagination || nextPage > lastPage) return;

    const el = chatBodyRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    const prevTop = el ? el.scrollTop : 0;

    loadOlderLockRef.current = true;
    try {
      setChatLoadingOlder(true);
      const { messages, pagination } = await ChatController.getMessages({
        page: nextPage,
        perPage: 20,
      });
      if (!messages || messages.length === 0) {
        return;
      }
      chatMaxLoadedPageRef.current = nextPage;
      setChatPagination(pagination);
      const enriched = enrichMessagesWithSentCache(messages, recentSentMessagesRef.current);
      setChatMessages((prev) => mergeMessagesById(prev, enriched));
      requestAnimationFrame(() => {
        const box = chatBodyRef.current;
        if (box) {
          const delta = box.scrollHeight - prevHeight;
          box.scrollTop = prevTop + delta;
        }
      });
    } catch (_) {
    } finally {
      setChatLoadingOlder(false);
      loadOlderLockRef.current = false;
    }
  };

  const onChatBodyScroll = () => {
    const el = chatBodyRef.current;
    if (!el || chatLoading || chatLoadingOlder) return;
    if (!allowLoadOlderRef.current) return;
    if (forceScrollToLatestRef.current) return;
    if (el.scrollTop > 72) return;
    loadOlderMessages();
  };

  const handleOpenChat = () => {
    setIsChatOpen((o) => !o);
  };

  const handleSendChatMessage = async (event) => {
    event.preventDefault();
    if (!chatInput.trim() || chatSending) return;
    const trimmedMessage = chatInput.trim();

    try {
      setChatSending(true);
      const sentEntry = { text: trimmedMessage, sentAt: Date.now() };
      const updatedSent = [...recentSentMessagesRef.current, sentEntry].slice(-100);
      recentSentMessagesRef.current = updatedSent;
      persistRecentSentMessages(updatedSent);

      const storedUserId =
        localStorage.getItem("userId") || sessionStorage.getItem("userId") || null;

      await ChatController.sendMessage({
        message: trimmedMessage,
        userId: storedUserId,
      });

      setChatInput("");
      try {
        const { messages, pagination } = await ChatController.getMessages({
          page: 1,
          perPage: 20,
        });
        setChatPagination((p) => ({ ...(p || {}), ...pagination }));
        const enriched = enrichMessagesWithSentCache(messages, recentSentMessagesRef.current);
        setChatMessages((prev) => mergeMessagesById(prev, enriched));
        requestAnimationFrame(() => scrollToBottomForced());
      } catch (_) {}
    } catch (err) {
      showErrorRef.current?.("Chat Error", err?.message || "Failed to send message.", {
        skipLogout: true,
      });
    } finally {
      setChatSending(false);
    }
  };

  const refreshLatestMessages = async () => {
    const { messages, pagination } = await ChatController.getMessages({
      page: 1,
      perPage: 20,
    });
    setChatPagination((p) => ({ ...(p || {}), ...pagination }));
    const enriched = enrichMessagesWithSentCache(messages, recentSentMessagesRef.current);
    setChatMessages((prev) => mergeMessagesById(prev, enriched));
    requestAnimationFrame(() => scrollToBottomForced());
  };

  const handlePickAttachment = () => {
    if (chatUploadingAttachment || chatSending) return;
    fileInputRef.current?.click();
  };

  const handleAttachmentChange = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    try {
      setChatUploadingAttachment(true);
      await ChatController.uploadChatDocument({ file });
      await refreshLatestMessages();
    } catch (err) {
      showErrorRef.current?.(
        "Chat Error",
        err?.message || "Failed to upload attachment."
      );
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setChatUploadingAttachment(false);
    }
  };

  const handleOpenAttachment = async (documentName) => {
    if (!documentName) return;
    try {
      const { url } = await ChatController.getChatDocumentUrl(documentName);
      const link = document.createElement("a");
      link.href = url;
      link.download = documentName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      showErrorRef.current?.(
        "Chat Error",
        err?.message || "Failed to download attachment."
      );
    }
  };

  if (hideLauncher && !isChatOpen) {
    return null;
  }

  return (
    <div className="support-chat-container">
      {!hideLauncher && (
        <button
          type="button"
          className="support-chat-button"
          aria-label="Support"
          onClick={handleOpenChat}
        >
          <i className="fa-solid fa-comment-dots" />
          {unreadCount > 0 && (
            <span className="support-chat-badge" aria-label={`${unreadCount} unread messages`}>
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      )}

      {isChatOpen && (
        <div
          className={`support-chat-window${
            hideLauncher ? " support-chat-window--solo" : ""
          }`}
        >
          <div className="support-chat-header">
            <div className="support-chat-title">Support Chat</div>
            <button
              type="button"
              className="support-chat-close"
              onClick={() => setIsChatOpen(false)}
              aria-label="Close chat"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          <div
            className="support-chat-body"
            ref={chatBodyRef}
            onScroll={onChatBodyScroll}
          >
            <div className="support-chat-note">
              Please note: replies are handled by our support team.
            </div>
            {chatLoadingOlder && (
              <div className="support-chat-status support-chat-status--inline">
                Loading older messages…
              </div>
            )}
            {chatLoading ? (
              <div className="support-chat-status">Loading messages...</div>
            ) : chatMessages.length === 0 ? (
              <div className="support-chat-status">
                No messages yet. Start a conversation with support.
              </div>
            ) : (
              <>
                {resolvedChatMessages.map((msg) => {
                  const label = msg.isMe
                    ? msg.senderNickName?.trim() || "You"
                    : msg.senderNickName?.trim() || "Support";
                  const preview = (msg.text || "").slice(0, 180);
                  const isDocumentMessage = Number(msg.messageType) === 3;
                  return (
                  <div
                    key={msg.id || `${msg.createdAt}-${msg.text}`}
                    role="article"
                    aria-label={`${msg.isMe ? "You" : "Support"}: ${preview}`}
                    className={`support-chat-message ${
                      msg.isMe ? "support-chat-message--me" : "support-chat-message--support"
                    } ${msg.firstInSequence ? "support-chat-message--first" : "support-chat-message--continue"}`}
                  >
                    {msg.firstInSequence ? (
                      <div
                        className={`support-chat-sender-name${
                          msg.isMe ? " support-chat-sender-name--me" : ""
                        }`}
                      >
                        {label}
                      </div>
                    ) : null}
                    {isDocumentMessage ? (
                      <button
                        type="button"
                        className="support-chat-attachment"
                        onClick={() => handleOpenAttachment(msg.text)}
                      >
                        <i className="fa-solid fa-paperclip" />
                        <span>{msg.text || "Attachment"}</span>
                      </button>
                    ) : (
                      <div className="support-chat-message-text">{msg.text}</div>
                    )}
                    {msg.createdAt && (
                      <div className="support-chat-message-time">
                        {new Date(msg.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    )}
                  </div>
                );
                })}
                <div
                  ref={chatBottomSentinelRef}
                  className="support-chat-bottom-sentinel"
                  aria-hidden="true"
                />
              </>
            )}
          </div>

          <form className="support-chat-input-row" onSubmit={handleSendChatMessage}>
            <input
              ref={fileInputRef}
              type="file"
              className="support-chat-file-input"
              onChange={handleAttachmentChange}
            />
            <button
              type="button"
              className="support-chat-attach"
              onClick={handlePickAttachment}
              disabled={chatSending || chatUploadingAttachment}
              aria-label="Attach file"
              title="Attach file"
            >
              <i className="fa-solid fa-paperclip" />
            </button>
            <textarea
              ref={chatInputRef}
              rows={1}
              className="support-chat-input"
              placeholder="Type your message..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
              disabled={chatSending}
            />
            <button
              type="submit"
              className="support-chat-send"
              disabled={chatSending || chatUploadingAttachment || !chatInput.trim()}
            >
              {chatSending ? "Sending..." : chatUploadingAttachment ? "Uploading..." : "Send"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
